import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, AlertCircle, Users, ChevronLeft } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';

// ── Colour tokens ─────────────────────────────────────────────────────────────

const C = {
  yellow:      '#F7D000',
  black:       '#1A1A1A',
  white:       '#FFFFFF',
  canvas:      '#F9FAFB',
  border:      '#E5E7EB',
  gray50:      '#F9FAFB',
  gray100:     '#F3F4F6',
  gray200:     '#E5E7EB',
  gray500:     '#6B7280',
  gray700:     '#374151',
  gray900:     '#111827',
  success:     '#2E7D32',
  successBg:   '#DCFCE7',
  successText: '#166534',
  error:       '#DC2626',
  errorBg:     '#FEE2E2',
  errorText:   '#991B1B',
  warningBg:   '#FEF3C7',
  warningText: '#92400E',
  infoBg:      '#DBEAFE',
  infoText:    '#1E40AF',
  orangeBg:    '#FFEDD5',
  orangeText:  '#9A3412',
};

const cardShadow = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';

// ── Text ──────────────────────────────────────────────────────────────────────

const T = {
  ms: {
    titlePick: 'Rekod Kehadiran', titleMark: 'Tandakan Kehadiran',
    subtitlePick: 'Mark Attendance',
    changeSession: 'Tukar Sesi', classLabel: 'Kelas', dateLabel: 'Tarikh',
    periodLabel: 'Waktu', startCreating: 'Membuat sesi…',
    startButton: 'Mula Rekod Kehadiran',
    setAll: 'Tetapkan semua:', noClasses: 'Tiada kelas dijumpai untuk sekolah ini.',
    noStudents: 'Tiada pelajar aktif dalam kelas ini.',
    retryButton: 'Cuba Lagi', errorLabel: 'Ralat:',
    students: 'pelajar', absent: 'tidak hadir',
    absentAlert: 'Amaran pemberitahuan akan dijana untuk setiap ketidakhadiran.',
    saving: 'Menyimpan…', saveButton: 'Simpan Kehadiran',
    present: 'Hadir', late: 'Lewat', sick: 'Sakit',
    excused: 'MC/Cuti', unexcused: 'Tanpa Alasan',
    progress: 'Kemajuan',
  },
  en: {
    titlePick: 'Attendance', titleMark: 'Mark Attendance',
    subtitlePick: 'Select class and date to begin',
    changeSession: 'Change Session', classLabel: 'Class', dateLabel: 'Date',
    periodLabel: 'Period', startCreating: 'Creating session…',
    startButton: 'Start Marking',
    setAll: 'Set all:', noClasses: 'No classes found for this school.',
    noStudents: 'No active students in this class.',
    retryButton: 'Retry', errorLabel: 'Error:',
    students: 'students', absent: 'absent',
    absentAlert: 'Absence alerts will be created for each absent student.',
    saving: 'Saving…', saveButton: 'Save Attendance',
    present: 'Present', late: 'Late', sick: 'Absent Sick',
    excused: 'MC/Excused', unexcused: 'Unexcused',
    progress: 'Progress',
  },
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props { user: AuthUser }
interface SchoolClass { id: string; name: string; level: string; school_id: string }
interface Student {
  id: string; registration_number: string;
  full_name: string; ic_number: string | null;
  record_id: string | null; mark: string | null;
}
interface SessionStudents {
  session: { id: string; date: string; class_id: string; status: string };
  students: Student[];
}
type MarkValue = 'PRESENT' | 'LATE' | 'ABSENT_SICK' | 'ABSENT_EXCUSED' | 'ABSENT_UNEXCUSED';

function getSchoolId(user: AuthUser): string | null {
  if (user.roles.some((r) => r.role === 'super_admin' && r.schoolId === null)) return null;
  return user.roles.find((r) => r.schoolId)?.schoolId ?? null;
}

// ── Mark options config ───────────────────────────────────────────────────────

const MARK_STYLES: Record<MarkValue, { bg: string; text: string; border: string }> = {
  PRESENT:          { bg: C.successBg, text: C.successText, border: '#86EFAC' },
  LATE:             { bg: C.warningBg, text: C.warningText, border: '#FDE68A' },
  ABSENT_SICK:      { bg: C.orangeBg,  text: C.orangeText,  border: '#FDBA74' },
  ABSENT_EXCUSED:   { bg: C.infoBg,    text: C.infoText,    border: '#93C5FD' },
  ABSENT_UNEXCUSED: { bg: C.errorBg,   text: C.errorText,   border: '#FCA5A5' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SelectInput({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: C.gray700, marginBottom: '6px' }}>
        {label}{required && <span style={{ color: C.error, marginLeft: '2px' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  width: '100%', backgroundColor: C.white,
  border: `1.5px solid ${C.border}`, borderRadius: '8px',
  color: C.gray900, padding: '10px 14px',
  fontSize: '14px', outline: 'none', cursor: 'pointer',
  fontFamily: '"Inter", sans-serif',
};

const inputStyle: React.CSSProperties = {
  width: '100%', backgroundColor: C.white,
  border: `1.5px solid ${C.border}`, borderRadius: '8px',
  color: C.gray900, padding: '10px 14px',
  fontSize: '14px', outline: 'none',
  fontFamily: '"Inter", sans-serif',
};

// ── Main component ────────────────────────────────────────────────────────────

export function AttendancePage({ user }: Props) {
  const navigate  = useNavigate();
  const { lang }  = useLang();
  const UI        = T[lang === 'en' ? 'en' : 'ms'];
  const schoolId  = getSchoolId(user);

  usePageTitle(lang === 'en' ? 'Attendance' : 'Kehadiran / Attendance');

  const MARK_OPTIONS: Array<{ value: MarkValue; label: string }> = [
    { value: 'PRESENT',          label: UI.present },
    { value: 'LATE',             label: UI.late },
    { value: 'ABSENT_SICK',      label: UI.sick },
    { value: 'ABSENT_EXCUSED',   label: UI.excused },
    { value: 'ABSENT_UNEXCUSED', label: UI.unexcused },
  ];

  const [classes,        setClasses]        = useState<SchoolClass[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError,   setClassesError]   = useState<string | null>(null);
  const [selectedClass,  setSelectedClass]  = useState('');
  const [selectedDate,   setSelectedDate]   = useState(() => new Date().toISOString().slice(0, 10));
  const [periodNumber,   setPeriodNumber]   = useState(1);
  const [phase,          setPhase]          = useState<'pick' | 'mark'>('pick');
  const [session,        setSession]        = useState<SessionStudents | null>(null);
  const [marks,          setMarks]          = useState<Record<string, MarkValue>>({});
  const [creating,       setCreating]       = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [toast,          setToast]          = useState<{ message: string; variant: ToastVariant } | null>(null);

  const loadClasses = useCallback(async () => {
    const sid = schoolId;
    if (!sid && !user.roles.some((r) => r.role === 'super_admin')) {
      setClassesError(lang === 'en' ? 'No school assigned to your account.' : 'Tiada sekolah ditetapkan untuk akaun anda.');
      return;
    }
    setClassesLoading(true); setClassesError(null);
    try {
      const effectiveSid = sid ?? '3f868b12-b554-4f51-a893-69e9b4ba9f57';
      const data = await apiGet<SchoolClass[]>(`/api/students/classes?schoolId=${effectiveSid}`);
      setClasses(data);
      if (data.length > 0 && !selectedClass) setSelectedClass(data[0]!.id);
    } catch (err) {
      setClassesError(err instanceof Error ? err.message : lang === 'en' ? 'Unknown error.' : 'Ralat tidak diketahui.');
    } finally {
      setClassesLoading(false);
    }
  }, [schoolId, user.roles, selectedClass, lang]);

  useEffect(() => { void loadClasses(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreateSession() {
    if (!selectedClass || !selectedDate || creating) return;
    setCreating(true);
    try {
      const { id } = await apiPost<{ id: string }>('/api/students/sessions', {
        classId: selectedClass, date: selectedDate, periodNumber,
      });
      const data = await apiGet<SessionStudents>(`/api/students/sessions/${id}/students`);
      setSession(data);
      const defaults: Record<string, MarkValue> = {};
      for (const s of data.students) defaults[s.id] = (s.mark as MarkValue) ?? 'PRESENT';
      setMarks(defaults);
      setPhase('mark');
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Ralat tidak diketahui.' });
    } finally { setCreating(false); }
  }

  async function handleSubmit() {
    if (!session || submitting) return;
    const records = session.students.map((s) => ({
      studentId: s.id, mark: marks[s.id] ?? 'PRESENT', checkInMethod: 'MANUAL',
    }));
    setSubmitting(true);
    try {
      const result = await apiPost<{ success: boolean; absentCount: number; alertsCreated: number }>(
        `/api/students/sessions/${session.session.id}/submit`, { records },
      );
      setToast({
        variant: 'success',
        message: lang === 'en'
          ? `Saved. ${result.absentCount} absent, ${result.alertsCreated} alert(s) created.`
          : `Disimpan. ${result.absentCount} tidak hadir, ${result.alertsCreated} amaran dibuat.`,
      });
      setTimeout(() => navigate('/admin/attendance/alerts'), 1800);
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Ralat.' });
    } finally { setSubmitting(false); }
  }

  const absentCount  = Object.values(marks).filter((m) => m.startsWith('ABSENT')).length;
  const markedCount  = Object.keys(marks).length;
  const totalCount   = session?.students.length ?? 0;
  const progressPct  = totalCount > 0 ? Math.round((markedCount / totalCount) * 100) : 0;

  return (
    <div style={{ maxWidth: '760px' }}>
      {/* Phase breadcrumb */}
      {phase === 'mark' && (
        <button
          onClick={() => { setPhase('pick'); setSession(null); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.gray500, fontSize: '14px', padding: '0 0 16px',
            fontFamily: '"Inter", sans-serif',
          }}
        >
          <ChevronLeft size={16} />
          {UI.changeSession}
        </button>
      )}

      {/* Page header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: C.gray900, marginBottom: '4px', fontFamily: '"Noto Sans", sans-serif' }}>
          {phase === 'pick' ? UI.titlePick : UI.titleMark}
        </h1>
        <p style={{ fontSize: '14px', color: C.gray500 }}>
          {phase === 'pick'
            ? UI.subtitlePick
            : `${totalCount} ${UI.students} · ${absentCount} ${UI.absent}`}
        </p>
      </div>

      {/* ── Phase 1: Class + date picker ── */}
      {phase === 'pick' && (
        <div
          style={{
            backgroundColor: C.white, borderRadius: '12px',
            border: `1px solid ${C.border}`, padding: '28px',
            boxShadow: cardShadow,
          }}
        >
          {classesError ? (
            <div
              style={{
                backgroundColor: C.errorBg, border: `1px solid #FECACA`,
                borderLeft: `4px solid ${C.error}`,
                borderRadius: '8px', padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '16px',
              }}
            >
              <AlertCircle size={18} color={C.error} strokeWidth={2} />
              <div>
                <p style={{ margin: 0, fontSize: '14px', color: C.errorText, fontWeight: 600 }}>{UI.errorLabel}</p>
                <p style={{ margin: 0, fontSize: '13px', color: C.errorText }}>{classesError}</p>
              </div>
              <button
                onClick={() => void loadClasses()}
                style={{
                  marginLeft: 'auto', backgroundColor: C.error, color: C.white,
                  border: 'none', borderRadius: '6px', padding: '6px 14px',
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                {UI.retryButton}
              </button>
            </div>
          ) : classesLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px', gap: '12px' }}>
              <span
                style={{
                  width: '24px', height: '24px',
                  border: `3px solid ${C.border}`, borderTopColor: C.yellow,
                  borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block',
                }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <span style={{ color: C.gray500, fontSize: '14px' }}>
                {lang === 'en' ? 'Loading classes…' : 'Memuatkan kelas…'}
              </span>
            </div>
          ) : classes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: C.gray500 }}>
              <Users size={40} color={C.gray200} style={{ marginBottom: '12px' }} />
              <p style={{ fontSize: '14px' }}>{UI.noClasses}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <SelectInput label={UI.classLabel} required>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    style={selectStyle} disabled={creating}
                  >
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.level})</option>
                    ))}
                  </select>
                </SelectInput>

                <SelectInput label={UI.dateLabel} required>
                  <input
                    type="date"
                    value={selectedDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    style={inputStyle} disabled={creating}
                  />
                </SelectInput>
              </div>

              <SelectInput label={UI.periodLabel}>
                <select
                  value={periodNumber}
                  onChange={(e) => setPeriodNumber(Number(e.target.value))}
                  style={selectStyle} disabled={creating}
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{UI.periodLabel} {n}</option>
                  ))}
                </select>
              </SelectInput>

              <button
                onClick={() => void handleCreateSession()}
                disabled={!selectedClass || !selectedDate || creating}
                style={{
                  backgroundColor: C.yellow, color: C.black,
                  border: 'none', borderRadius: '8px',
                  padding: '12px 20px', fontSize: '15px', fontWeight: 700,
                  cursor: creating || !selectedClass ? 'not-allowed' : 'pointer',
                  opacity: creating || !selectedClass ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                {creating ? (
                  <>
                    <span style={{ width: '16px', height: '16px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: C.black, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                    {UI.startCreating}
                  </>
                ) : (
                  <>
                    <CalendarCheck size={18} strokeWidth={2} />
                    {UI.startButton}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Phase 2: Mark students ── */}
      {phase === 'mark' && session && (
        <>
          {/* Progress bar */}
          <div
            style={{
              backgroundColor: C.white, borderRadius: '12px',
              border: `1px solid ${C.border}`, padding: '16px 20px',
              marginBottom: '16px', boxShadow: cardShadow,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: C.gray500, fontWeight: 500 }}>{UI.progress}</span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: C.gray900 }}>
                {markedCount}/{totalCount}
              </span>
            </div>
            <div style={{ height: '8px', backgroundColor: C.gray100, borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, backgroundColor: C.yellow, borderRadius: '999px', transition: 'width 0.2s' }} />
            </div>
          </div>

          {session.students.length === 0 ? (
            <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '48px', textAlign: 'center' }}>
              <Users size={40} color={C.gray200} style={{ marginBottom: '12px' }} />
              <p style={{ color: C.gray500 }}>{UI.noStudents}</p>
            </div>
          ) : (
            <div
              style={{
                backgroundColor: C.white, borderRadius: '12px',
                border: `1px solid ${C.border}`, overflow: 'hidden',
                boxShadow: cardShadow, marginBottom: '16px',
              }}
            >
              {/* Quick-set bar */}
              <div
                style={{
                  padding: '12px 20px', borderBottom: `1px solid ${C.border}`,
                  backgroundColor: C.gray50,
                  display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: '12px', color: C.gray500, fontWeight: 600, marginRight: '4px' }}>
                  {UI.setAll}
                </span>
                {MARK_OPTIONS.map((opt) => {
                  const s = MARK_STYLES[opt.value];
                  return (
                    <button
                      key={opt.value}
                      onClick={() => {
                        const all: Record<string, MarkValue> = {};
                        session.students.forEach((st) => { all[st.id] = opt.value; });
                        setMarks(all);
                      }}
                      disabled={submitting}
                      style={{
                        backgroundColor: s.bg, color: s.text,
                        border: `1px solid ${s.border}`,
                        borderRadius: '999px', padding: '3px 12px',
                        fontSize: '12px', fontWeight: 600,
                        cursor: 'pointer', fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Student rows */}
              {session.students.map((student, i) => {
                const currentMark = marks[student.id] ?? 'PRESENT';
                return (
                  <div
                    key={student.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 20px',
                      borderBottom: i < session.students.length - 1 ? `1px solid ${C.border}` : 'none',
                      backgroundColor: i % 2 === 0 ? C.white : '#FAFAFA',
                      opacity: submitting ? 0.6 : 1,
                    }}
                  >
                    {/* Avatar initials */}
                    <div
                      style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        backgroundColor: '#F3F4F6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontWeight: 700, color: C.gray500,
                        flexShrink: 0,
                      }}
                    >
                      {student.full_name.split(' ').slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase()}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: C.gray900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {student.full_name}
                      </div>
                      {student.registration_number && (
                        <div style={{ fontSize: '12px', color: C.gray500 }}>
                          {student.registration_number}
                        </div>
                      )}
                    </div>

                    {/* Mark buttons */}
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {MARK_OPTIONS.map((opt) => {
                        const active = currentMark === opt.value;
                        const s = MARK_STYLES[opt.value];
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setMarks((prev) => ({ ...prev, [student.id]: opt.value }))}
                            disabled={submitting}
                            title={opt.label}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: active ? s.bg : 'transparent',
                              border: `1.5px solid ${active ? s.border : C.border}`,
                              color: active ? s.text : C.gray500,
                              borderRadius: '999px', fontSize: '12px',
                              fontWeight: active ? 700 : 500,
                              cursor: 'pointer', transition: 'all 0.1s',
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sticky submit bar */}
          {session.students.length > 0 && (
            <div
              style={{
                position: 'sticky', bottom: '0',
                backgroundColor: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: '12px',
                padding: '16px 20px',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '16px', flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '14px', color: C.gray700 }}>
                    <strong style={{ color: C.gray900 }}>{totalCount}</strong> {UI.students}
                  </span>
                  <span style={{ fontSize: '14px', color: absentCount > 0 ? C.error : C.success }}>
                    <strong>{absentCount}</strong> {UI.absent}
                  </span>
                </div>
                {absentCount > 0 && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.gray500 }}>
                    {UI.absentAlert}
                  </p>
                )}
              </div>
              <button
                onClick={() => void handleSubmit()}
                disabled={submitting}
                style={{
                  backgroundColor: C.yellow, color: C.black,
                  border: 'none', borderRadius: '8px',
                  padding: '12px 24px', fontSize: '15px', fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontFamily: '"Inter", sans-serif',
                  whiteSpace: 'nowrap',
                }}
              >
                {submitting ? (
                  <>
                    <span style={{ width: '16px', height: '16px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: C.black, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                    {UI.saving}
                  </>
                ) : (
                  <>
                    <CalendarCheck size={18} strokeWidth={2} />
                    {UI.saveButton}
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </div>
  );
}
